import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LessonCreatorSidebar.module.css';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';

const STEPS = [
  { id: 'lessonInfo', key: 'lesson_info' },
  { id: 'connections', key: 'connections' },
  { id: 'lessonEditor', key: 'lesson_editor' },
];

/**
 * Card in top-left only (does not extend to bottom). TOOLS navigates back (optional).
 * currentStep: 'lessonInfo' | 'connections' | 'lessonEditor'
 * hideToolsButton: when true (e.g. on select-elements), do not show the Tools button.
 * onStepClick(stepId): when provided, completed steps are clickable links; called with step id (e.g. 'lessonInfo').
 */
function LessonCreatorSidebar({ currentStep = 'lessonInfo', hideToolsButton = false, onStepClick }) {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const handleToolsClick = () => {
    navigate(-1);
  };

  const currentIndex = STEPS.findIndex((s) => s.id === currentStep);

  const getStepStyle = (stepId) => {
    const stepIndex = STEPS.findIndex((s) => s.id === stepId);
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

  /* Connector line: green down to end of current step, then gray (surface_500) for future steps */
  const stepPercent = STEPS.length > 0 ? ((currentIndex + 1) / STEPS.length) * 100 : 100;

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarTop}>
        {!hideToolsButton && (
          <button
            type="button"
            className={styles.toolsButton}
            onClick={handleToolsClick}
            aria-label="Back to Tools"
          >
            <span className={styles.toolsChevron}>&lt;</span>
            <span className={styles.toolsLabel}>tools</span>
          </button>
        )}

        <div className={styles.lessonCreatorBlock}>
          <div className={styles.heading}>{t('lesson_creator', language)}</div>
          <div
            className={styles.stepsList}
            style={{ '--step-percent': `${stepPercent}%` }}
          >
            {STEPS.map((step) => {
              const stepStyle = getStepStyle(step.id);
              const isCompleted = stepStyle === 'completed';
              const isClickable = isCompleted && typeof onStepClick === 'function';
              const handleStepClick = () => {
                if (isClickable) onStepClick(step.id);
              };
              return (
                <div key={step.id} className={styles.stepRow}>
                  <div className={`${styles.stepIcon} ${styles[stepStyle]}`}>
                    {isCompleted && <img src="/assets/Tick_Icon.png" alt="" className={styles.checkmark} />}
                  </div>
                  {isClickable ? (
                    <button
                      type="button"
                      className={`${styles.stepLabel} ${styles.stepLabelLink}`}
                      onClick={handleStepClick}
                    >
                      {t(step.key, language)}
                    </button>
                  ) : (
                    <div className={`${styles.stepLabel} ${stepStyle === 'pending' ? styles.pending : ''} ${stepStyle === 'current' ? styles.current : ''}`}>
                      {t(step.key, language)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </aside>
  );
}

export default LessonCreatorSidebar;
